// File copied from packages/editor/editor-markdown-transformer/src/media.ts

import { MEDIA_BASE_URL_STAGING } from '../../../convert-prosemirror-to-markdown/pm-markdown/utils/constants';

export type Token = {
	new (type: string, tag: string, level: number): Token;
	attrs?: string[][];
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	children?: any[];
	content: string;
	level: number;
	tag: string;
	type: string;
};

export interface MdState {
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	md: any;
	Token: Token;
	tokens: Token[];
}

function createRule() {
	// Ignored via go/ees005
	// eslint-disable-next-line require-unicode-regexp
	const regx = /!\[[^\]]*\]\([^)]+\)/g;
	// Allow paragraph_close as a valid parent token for cases where a user has an image at the end of a paragraph
	// due to shift+enter
	const validParentTokens = ['th_open', 'td_open', 'list_item_open', 'paragraph_close'];

	/**
	 * This function looks for strings that matches ![description](url) inside Inline-tokens.
	 * It will then split the Inline-token with content before and after the image, example:
	 *
	 * Input (tokens):
	 *   paragraph_open
	 *   inline - content: Hello ![](image.jpg) World!
	 *   paragraph_close
	 * Output (tokens):
	 *   paragraph_open
	 *   inline - content: Hello
	 *   paragraph_close
	 *   media_single_open
	 *   media
	 *   media_single_close
	 *   paragraph_open
	 *   inline - content: World!
	 *   paragraph_close
	 *
	 * This is applied before the inline content is parsed, to ensure that the formatting of
	 * remaining inline content (bold, links, etc.) is kept intact!
	 */
	return function media(State: MdState) {
		const getUrl = (str: string) => {
			const res = State.md.helpers.parseLinkDestination(str, str.indexOf('(') + 1, str.length);
			if (res.ok) {
				const href = State.md.normalizeLink(res.str);
				if (State.md.validateLink(href)) {
					return href;
				}
			}

			return '';
		};

		const createMediaTokens = (url: string) => {
			const mediaSingleOpen = new State.Token('media_single_open', '', 1);
			const media = new State.Token('media', '', 0);
			media.attrs = [
				['url', getUrl(url)],
				['type', 'external'],
			];

			if (url.includes(MEDIA_BASE_URL_STAGING)) {
				try {
					const urlObject = new URL(getUrl(url));
					const params = urlObject.searchParams;

					if (params.toString()) {
						const pmAttrs: { [key: string]: string } = {};
						for (const [key, value] of params) {
							pmAttrs[key] = value;
						}

						// @ts-expect-error - pmAttrs is not a standard markdown-it attribute
						media.pmAttrs = pmAttrs;
					}
				} catch {}
			}

			const mediaSingleClose = new State.Token('media_single_close', '', -1);

			return [mediaSingleOpen, media, mediaSingleClose];
		};

		const createInlineTokens = (str: string, openingTokens: Token[], closingTokens: Token[]) => {
			if (!str || str.length === 0) {
				return [];
			}

			const inlineBefore = new State.Token('inline', '', 1);
			inlineBefore.content = str;
			inlineBefore.children = [];

			return [...openingTokens, inlineBefore, ...closingTokens];
		};

		let processedTokens: string[] = [];
		const newTokens = State.tokens.reduce(
			(tokens: Token[], token: Token, i: number, arr: Token[]) => {
				if (token.type === 'inline' && regx.test(token.content)) {
					const openingTokens: Token[] = [];
					let cursor = i - 1;
					let previousToken = arr[cursor];
					let subTree: Token[] = [];

					while (
						previousToken &&
						previousToken.level > 0 &&
						validParentTokens.indexOf(previousToken.type) === -1
					) {
						openingTokens.unshift(previousToken);
						cursor--;
						previousToken = arr[cursor];
					}

					if (validParentTokens.indexOf(previousToken.type) === -1) {
						openingTokens.unshift(previousToken);
					} else {
						cursor++;
					}

					const closingTokens = openingTokens
						.map((token) => new State.Token(token.type.replace('_open', '_close'), token.tag, -1))
						.reverse();

					// Ignored via go/ees005
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					const matches = token.content.match(regx)!;
					let inlineContentStack = token.content;
					matches.forEach((match) => {
						const start = inlineContentStack.indexOf(match);
						const contentBefore = inlineContentStack.substr(0, start);
						inlineContentStack = inlineContentStack.substr(start + match.length);

						subTree = [
							...subTree,
							...createInlineTokens(contentBefore, openingTokens, closingTokens),
							...createMediaTokens(match),
						];
					});

					if (inlineContentStack.length) {
						subTree = [
							...subTree,
							...createInlineTokens(inlineContentStack, openingTokens, closingTokens),
						];
					}

					processedTokens = [...processedTokens, ...closingTokens.map((c) => c.type)];

					tokens = [...tokens.slice(0, cursor), ...subTree];
				} else if (processedTokens.indexOf(token.type) !== -1) {
					// Ignore token if it's already processed
					processedTokens.splice(processedTokens.indexOf(token.type), 1);
				} else {
					tokens.push(token);
				}

				return tokens;
			},
			[],
		);

		State.tokens = newTokens;
		return true;
	};
}

// Ignored via go/ees005
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const markdownItMedia = (md: any) => {
	md.core.ruler.before('inline', 'media', createRule());
};
